import { ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location])

  return (
    <div className="min-h-screen bg-background text-primary font-sans">
      {/* Header */}
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? 'bg-background/80 backdrop-blur-md border-b border-white/5 py-4' : 'bg-transparent py-6'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center hover:opacity-80 transition-opacity relative z-50"
            >
              <img
                src="/logo-icon.png"
                alt="CHIPAHUAK"
                className="h-8 w-auto brightness-0 invert"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-10">
              <Link
                to="/portfolio"
                className="text-sm font-medium text-neutral-400 hover:text-white transition-colors uppercase tracking-wider"
              >
                Work
              </Link>
              <Link
                to="/case-studies"
                className="text-sm font-medium text-neutral-400 hover:text-white transition-colors uppercase tracking-wider"
              >
                Case Studies
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium text-neutral-400 hover:text-white transition-colors uppercase tracking-wider"
              >
                About
              </Link>
              <a
                href="mailto:hello@chipahauk.com"
                className="px-6 py-2 bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
              >
                Contact
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-white relative z-50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <div className="w-6 flex flex-col items-end gap-1.5">
                <span className={`h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'w-6 rotate-45 translate-y-2' : 'w-6'}`} />
                <span className={`h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'w-4'}`} />
                <span className={`h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'w-6 -rotate-45 -translate-y-2' : 'w-5'}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black z-40 flex flex-col justify-center items-center"
            >
              <nav className="flex flex-col items-center gap-8">
                <Link
                  to="/portfolio"
                  className="text-3xl font-display font-medium text-white hover:text-accent transition-colors"
                >
                  Work
                </Link>
                <Link
                  to="/case-studies"
                  className="text-3xl font-display font-medium text-white hover:text-accent transition-colors"
                >
                  Case Studies
                </Link>
                <Link
                  to="/about"
                  className="text-3xl font-display font-medium text-white hover:text-accent transition-colors"
                >
                  About
                </Link>
                <a
                  href="mailto:hello@chipahauk.com"
                  className="mt-8 px-8 py-3 bg-white text-black text-sm font-bold uppercase tracking-wider"
                >
                  Contact Us
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="relative border-t border-white/5 bg-background pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12 mb-20">
            {/* Brand */}
            <div className="md:col-span-5">
              <img
                src="/logo-full.png"
                alt="chipahuak"
                className="h-12 w-auto mb-8 brightness-0 invert opacity-50"
              />
              <p className="text-neutral-400 font-light leading-relaxed max-w-md">
                A creative solutions house exclusively for businesses. We deliver state-of-the-art design with exceptional speed and precision.
              </p>
            </div>

            {/* Navigation */}
            <div className="md:col-span-3">
              <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-6">Navigate</h3>
              <ul className="space-y-4">
                <li>
                  <Link to="/portfolio" className="text-neutral-400 hover:text-accent transition-colors text-sm">
                    Portfolio
                  </Link>
                </li>
                <li>
                  <Link to="/case-studies" className="text-neutral-400 hover:text-accent transition-colors text-sm">
                    Case Studies
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-neutral-400 hover:text-accent transition-colors text-sm">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="text-neutral-400 hover:text-accent transition-colors text-sm">
                    Services
                  </Link>
                </li>
                <li>
                  <Link to="/referral" className="text-neutral-400 hover:text-accent transition-colors text-sm">
                    Refer a Friend
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="md:col-span-4">
              <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-6">Contact</h3>
              <ul className="space-y-4 mb-8">
                <li>
                  <a
                    href="mailto:hello@chipahauk.com"
                    className="text-neutral-400 hover:text-white transition-colors text-sm flex items-center gap-3"
                  >
                    hello@chipahauk.com
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+17325016946"
                    className="text-neutral-400 hover:text-white transition-colors text-sm flex items-center gap-3"
                  >
                    732-501-6946
                  </a>
                </li>
              </ul>
              <div className="text-xs text-neutral-600 font-mono uppercase tracking-wider">
                <p>New Jersey, USA</p>
                <p className="mt-1">Global Reach</p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-neutral-600 font-mono uppercase tracking-wider">
            <p>© 2024 chipahuak. All rights reserved.</p>
            <p>Designed & Built with pride</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
